// The Main Nav Items (Product, Solutions ect.)
import type { ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'
import { DrawerArrow } from '~/components-v2/icons'
import clsx from 'clsx'
import type { StackProps } from '~/components-v2'
import { Stack } from '~/components-v2'
import { useClickAnyWhere } from 'usehooks-ts'
import { JSX } from 'react'

type DisclosureContentProps = Pick<
  StackProps,
  'className' | 'gap' | 'row' | 'wrap'
>

type DisclosureProps = {
  className?: string
  children: ReactNode
  megaMenu?: boolean
  title: string | JSX.Element
  contentProps?: DisclosureContentProps
  contentRef?: React.RefObject<HTMLDivElement>
}

const defaultContentProps: DisclosureContentProps = { gap: 0 }

export const Disclosure = ({
  className,
  title,
  children,
  megaMenu,
  contentProps = defaultContentProps,
  contentRef,
}: DisclosureProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  useClickAnyWhere((event) => {
    if (!isOpen) return
    if (!ref.current || !triggerRef.current) return
    if (ref.current.contains(event.target as Node)) return
    setIsOpen(false)
  })

  useEffect(() => {
    const main = document.getElementById('main-content')
    const el = ref.current

    if (!main || !el) return

    const isNested =
      el.closest('.nav-sub-column, .mobile-nav-subcolumn') !== null
    if (isNested) return

    if (isOpen) {
      el.setAttribute('data-disclosure-open', 'true')
    } else {
      el.removeAttribute('data-disclosure-open')
    }

    return () => {
      el.removeAttribute('data-disclosure-open')
    }
  }, [isOpen])

  return (
    <div
      ref={ref}
      className={clsx(className, megaMenu && 'mega-menu', isOpen && 'is-open')}
    >
      <button
        ref={triggerRef}
        className="disclosure-trigger"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span className="disclosure-label nav-m">{title}</span>
        <DrawerArrow aria-hidden className="nav-drawer-arrow" />
      </button>
      <Stack
        {...contentProps}
        row
        gap={0}
        className={clsx('disclosure-content', contentProps.className)}
        ref={contentRef}
      >
        {children}
      </Stack>
    </div>
  )
}
